import { useMemo } from "react";

import { buildDataEndpointSuffix } from "@/components/Templates/constants";
import { prepareUnifiedDataPostRequestBody } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import { useFetch } from "@/hooks/FetchHook";
import type { TableFilters } from "@/types/tableState.types";
import type { TableMetadataType } from "@/types/tableMetadata";

/**
 * A portfolio holds tens of trials, not thousands; one page this size covers
 * every real portfolio while keeping the response small.
 */
const TRIAL_ID_PAGE_SIZE = 500;

const hasActiveFilters = (filters: TableFilters | undefined): boolean =>
  Object.values(filters ?? {}).some((value) =>
    Array.isArray(value) ? value.length > 0 : value != null,
  );

/**
 * Resolves the trial ids a dataset's current filters select, independently of
 * which page the table happens to be showing. Returns null when no filters are
 * applied, meaning "everything is in scope" — callers then skip narrowing
 * rather than filtering against a set they did not need to fetch.
 */
export function useFilteredTrialIds({
  datasetName,
  identifierColumn,
  filters,
  metadata,
  endpointPrefix,
}: {
  datasetName: string;
  identifierColumn: string;
  filters: TableFilters | undefined;
  metadata: TableMetadataType | undefined;
  endpointPrefix?: string;
}): { trialIds: ReadonlySet<string> | null; isLoading: boolean } {
  const isFiltered = hasActiveFilters(filters);

  const payload = useMemo(
    () => ({
      dataset_type: datasetName,
      ...prepareUnifiedDataPostRequestBody({
        page: 1,
        pageSize: TRIAL_ID_PAGE_SIZE,
        global_filters: filters ?? {},
        allFiltersData: metadata?.filters,
      }),
    }),
    [datasetName, filters, metadata?.filters],
  );

  const { data, isLoading } = useFetch({
    endpointPrefix,
    endpointSuffix: buildDataEndpointSuffix(datasetName, endpointPrefix),
    method: "POST",
    body: payload,
    enabled: isFiltered,
    extraQueryKey: "filtered-trial-ids",
  });

  const trialIds = useMemo(() => {
    if (!isFiltered) return null;
    const { results } = (data ?? {}) as {
      results?: Record<string, unknown>[];
    };
    const rows = Array.isArray(results) ? results : [];
    return new Set(
      rows
        .map((row) => row?.[identifierColumn])
        .filter((value): value is string => typeof value === "string"),
    );
  }, [data, identifierColumn, isFiltered]);

  return { trialIds, isLoading: isFiltered && isLoading };
}
